import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaHardHat,
  FaMedkit,
  FaTruck,
  FaBolt,
  FaFlask,
  FaRoad,
  FaFireExtinguisher,
  FaBook,
  FaArrowRight
} from 'react-icons/fa';
import './TrainingGlobal.css'; // Reusing the global theme
import HeroImage from './training_hero.png'; // We will use a placeholder or existing image

const Training = () => {
  const navigate = useNavigate();

  // Course Data matching Navbar structure
  const trainingCategories = [
    {
      id: 'general',
      title: 'General Safety',
      icon: <FaHardHat className="category-icon" />,
      description: 'Essential safety protocols for every workplace environment.',
      courses: [
        { name: 'Environmental Safety', path: '/training/general/environmental' },
        { name: 'Safety Awareness', path: '/training/general/safety-awareness' },
        { name: 'Behaviour Based Safety', path: '/training/general/behaviour' },
        { name: '5S Concepts', path: '/training/general/sconcepts' },
        { name: 'Use of PPE', path: '/training/general/PPE' },
        { name: 'LOTO (Lock Out Tag Out)', path: '/training/general/LOTO' },
      ]
    },
    {
      id: 'health',
      title: 'Health Safety',
      icon: <FaMedkit className="category-icon" />,
      description: 'Promoting physical and mental well-being for employees.',
      courses: [
        { name: 'First Aid with Practical', path: '/training/health/firstaid' },
        { name: 'Heat and Cold Stress', path: '/training/health/heatcoldstress' },
        { name: 'Workplace Ergonomics', path: '/training/health/ergonomics' },
        { name: 'Mental Wellbeing', path: '/training/health/mental' },
      ]
    },
    {
      id: 'warehouse',
      title: 'Warehouse Safety',
      icon: <FaTruck className="category-icon" />,
      description: 'Specific safety measures for storage and logistics operations.',
      courses: [
        { name: 'Warehouse Safety', path: '/training/warehouse' },
        { name: 'MHE Driving Safety', path: '/training/MHE' },
        { name: 'Crane Safety', path: '/training/crane' },
      ]
    },
    {
      id: 'construction',
      title: 'Construction Safety',
      icon: <FaHardHat className="category-icon" />, // Reusing HardHat or could use another icon
      description: 'Critical safety standards for construction sites and heavy work.',
      courses: [
        { name: 'Construction Safety', path: '/training/constructionsafety' },
        { name: 'Working at Height', path: '/training/workingatheight' },
        { name: 'Hot Work Safety', path: '/training/hotwork' },
        { name: 'Excavation Safety', path: '/training/excavation' },
        { name: 'Confined Space Safety', path: '/training/confinedspace' },
      ]
    },
    {
      id: 'specialized',
      title: 'Specialized Safety',
      icon: <FaBolt className="category-icon" />,
      description: 'Advanced training for high-risk hazards and specific sectors.',
      courses: [
        { name: 'Electrical Safety', path: '/training/electrical', icon: <FaBolt /> },
        { name: 'Chemical Safety', path: '/training/chemical', icon: <FaFlask /> },
        { name: 'Road Safety', path: '/training/road', icon: <FaRoad /> },
      ]
    },
    {
      id: 'emergency',
      title: 'Emergency Preparedness',
      icon: <FaFireExtinguisher className="category-icon" />,
      description: 'Be ready for the unexpected with life-saving drills and knowledge.',
      courses: [
        { name: 'Fire Safety with Practical', path: '/training/firesafety' },
        { name: 'Emergency Preparedness', path: '/training/emergency' },
      ]
    },
    {
      id: 'iso',
      title: 'ISO Training',
      icon: <FaBook className="category-icon" />,
      description: 'Become a certified auditor and master ISO standards.',
      courses: [
        { name: 'ISO 9001 Auditor', path: '/training/isotraining/ISO9001' },
        { name: 'ISO 14001 Auditor', path: '/training/isotraining/ISO14001' },
        { name: 'ISO 45001 Auditor', path: '/training/isotraining/ISO45001' },
        { name: 'ISO 19011 Auditor', path: '/training/isotraining/ISO19011' },
        { name: 'ISO Documentation', path: '/training/isotraining/isodocumentation' },
        { name: 'HIRA and Control Methods', path: '/training/isotraining/HIRA' },
      ]
    },
  ];

  return (
    <div className="training-page">
      <div className="training-max-width">

        {/* --- HERO SECTION --- */}
        <div className="training-catalog-hero">
          <div className="hero-content-center">
            <span className="hero-tag">Professional Certification</span>
            <h1 className="hero-title">World-Class Safety Training</h1>
            <p className="hero-subtitle">
              Browse our comprehensive catalog of industry-standard safety courses.
              Empower your workforce with the knowledge to prevent accidents and save lives.
            </p>
          </div>
        </div>

        {/* --- CATALOG GRID --- */}
        <div className="catalog-grid">
          {trainingCategories.map((category) => (
            <div key={category.id} className="catalog-card">
              <div className="card-header">
                <div className="icon-wrapper">
                  {category.icon}
                </div>
                <h2 className="card-title">{category.title}</h2>
              </div>
              <p className="card-description">{category.description}</p>

              <div className="course-list">
                {category.courses.map((course, index) => (
                  <div
                    key={index}
                    className="course-item"
                    onClick={() => navigate(course.path)}
                  >
                    <span className="course-name">{course.name}</span>
                    <FaArrowRight className="arrow-icon" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* --- CTA BOTTOM --- */}
        <div className="catalog-cta">
          <h2>Need a Custom Training Solution?</h2>
          <p>We tailor onsite and offsite training programs to meet your specific industry needs.</p>
          <button className="cta-button" onClick={() => navigate('/contact')}>Get a Quote</button>
        </div>

      </div>

      {/* --- INLINE STYLES FOR LAYOUT (Moving to CSS ideally, but keeping self-contained for now or updating Global) --- */}
      <style>{`
        .training-catalog-hero {
          padding: 100px 20px;
          text-align: center;
          background: linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.8)), url(${HeroImage});
          background-size: cover;
          background-position: center;
          background-attachment: fixed;
        }
        .hero-content-center {
          max-width: 800px;
          margin: 0 auto;
        }
        .catalog-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 30px;
          padding: 40px 20px;
        }
        .catalog-card {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.05);
          border-radius: 16px;
          padding: 30px;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
        }
        .catalog-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5), 0 0 20px rgba(255, 107, 0, 0.1);
          border-color: rgba(255, 107, 0, 0.3);
        }
        .card-header {
          display: flex;
          align-items: center;
          gap: 15px;
          margin-bottom: 15px;
        }
        .icon-wrapper {
          width: 50px;
          height: 50px;
          border-radius: 12px;
          background: rgba(255, 107, 0, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #ff6b00;
          font-size: 1.5rem;
        }
        .card-title {
          font-family: 'Space Grotesk', sans-serif;
          font-size: 1.5rem;
          color: #fff;
          margin: 0;
        }
        .card-description {
          color: #aaa;
          margin-bottom: 25px;
          line-height: 1.6;
        }
        .course-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .course-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 15px;
          background: rgba(255, 255, 255, 0.02);
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .course-item:hover {
          background: rgba(255, 107, 0, 0.1);
          padding-left: 20px;
        }
        .course-name {
          color: #ddd;
          font-weight: 500;
        }
        .course-item:hover .course-name {
          color: #ff6b00;
        }
        .arrow-icon {
          color: #555;
          font-size: 0.8rem;
          transition: color 0.2s;
        }
        .course-item:hover .arrow-icon {
          color: #ff6b00;
        }
        .catalog-cta {
          margin: 60px 20px 20px;
          padding: 60px;
          background: linear-gradient(135deg, #1a1a1a 0%, #0d0d0d 100%);
          border-radius: 20px;
          text-align: center;
          border: 1px solid rgba(255, 255, 255, 0.05);
        }
        .catalog-cta h2 {
          font-family: 'Space Grotesk', sans-serif;
          font-size: 2rem;
          color: #fff;
          margin-bottom: 15px;
        }
        .catalog-cta p {
          color: #aaa;
          margin-bottom: 30px;
          font-size: 1.1rem;
        }
        .cta-button {
          padding: 15px 40px;
          background: linear-gradient(to right, #ff6b00, #ff8533);
          color: white;
          border: none;
          border-radius: 50px;
          font-weight: bold;
          font-size: 1.1rem;
          cursor: pointer;
          transition: transform 0.2s;
        }
        .cta-button:hover {
          transform: scale(1.05);
          box-shadow: 0 0 20px rgba(255, 107, 0, 0.4);
        }
      `}</style>
    </div>
  );
};

export default Training;